import { useEffect, useState } from 'react';
import { Card, Button, Input, Select, Field } from './ui';
import { api } from '../lib/api';
import type { ApiResponse } from '../lib/types';

type Provider = 'claude' | 'openai';
interface Model { id: string; name: string; tier?: string }
interface Setting { key: string; value: string; configured?: boolean }
interface Config { key: string; configured: boolean; model: string }
const names = { claude: 'Claude', openai: 'ChatGPT / OpenAI' };
const emptyConfig = (): Record<Provider, Config> => ({
  claude: { key: '', configured: false, model: 'claude-opus-5' },
  openai: { key: '', configured: false, model: 'gpt-6.1-sol' },
});

export function AISettings() {
  const [provider, setProvider] = useState<Provider>('claude');
  const [config, setConfig] = useState(emptyConfig);
  const [catalog, setCatalog] = useState<Record<Provider, Model[]>>({ claude: [], openai: [] });
  const [custom, setCustom] = useState<Record<Provider, boolean>>({ claude: false, openai: false });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState<'save' | 'test' | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const current = config[provider];
  const update = (patch: Partial<Config>) => setConfig(prev => ({ ...prev, [provider]: { ...prev[provider], ...patch } }));

  async function load() {
    setLoading(true); setReady(false); setMsg(null);
    try {
      const [settings, models] = await Promise.all([
        api.get<ApiResponse<Setting[]>>('/settings/get'),
        api.get<ApiResponse<Model[]> & { providers: Record<Provider, Model[]> }>('/settings/models'),
      ]);
      if (!settings.success || !models.success || !models.providers?.openai?.length || !models.providers?.claude?.length) {
        throw new Error('Could not load AI settings. Please retry.');
      }
      const next = emptyConfig();
      for (const p of ['claude', 'openai'] as const) {
        const key = settings.data?.find(s => s.key === `${p}_api_key`);
        next[p].configured = Boolean(key?.configured);
        next[p].model = settings.data?.find(s => s.key === `${p}_model`)?.value || models.providers[p][0].id;
      }
      setCatalog(models.providers); setConfig(next);
      setProvider(settings.data?.find(s => s.key === 'ai_provider')?.value === 'openai' ? 'openai' : 'claude');
      setReady(true);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : 'Could not load settings.' });
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  async function save() {
    setBusy('save'); setMsg(null);
    try {
      const settings: Setting[] = [{ key: 'ai_provider', value: provider }];
      for (const p of ['claude', 'openai'] as const) {
        settings.push({ key: `${p}_model`, value: config[p].model.trim() });
        if (config[p].key.trim()) settings.push({ key: `${p}_api_key`, value: config[p].key.trim() });
      }
      const result = await api.post<ApiResponse<unknown>>('/settings/save', { settings });
      if (!result.success) throw new Error(result.message || 'Failed to save settings.');
      setConfig(prev => ({
        claude: { ...prev.claude, key: '', configured: prev.claude.configured || Boolean(prev.claude.key.trim()) },
        openai: { ...prev.openai, key: '', configured: prev.openai.configured || Boolean(prev.openai.key.trim()) },
      }));
      setShow(false);
      setMsg({ ok: true, text: 'Settings saved.' });
    } catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : 'Failed to save.' }); }
    finally { setBusy(null); }
  }

  async function test() {
    if (!current.key.trim() && !current.configured) {
      setMsg({ ok: false, text: 'Enter the API key before testing.' }); return;
    }
    setBusy('test'); setMsg(null);
    try {
      const r = await api.post<ApiResponse<unknown> & { model?: string }>('/settings/test-ai', {
        provider, model: current.model.trim(), ...(current.key.trim() ? { api_key: current.key.trim() } : {}),
      });
      setMsg({ ok: r.success, text: r.success ? `Connection OK — ${r.model}` : (r.message || 'Connection failed.') });
    } catch (e) { setMsg({ ok: false, text: e instanceof Error ? e.message : 'Connection failed.' }); }
    finally { setBusy(null); }
  }

  return <Card className="p-6 max-w-3xl">
    {msg && <div role="status" className={`mb-4 rounded-lg text-sm px-4 py-3 border ${msg.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>{msg.text}</div>}
    {loading ? <p className="text-slate-400">Loading…</p> : <>
      {!ready && <Button onClick={load}>Retry loading settings</Button>}
      <fieldset disabled={!ready || !!busy} className="space-y-4 disabled:opacity-70">
        <Field label="AI Provider" help="Default for GTM generation, feed mapping and analyses of delivery, audience, monthly reports and fraud. The campaign editor can override the model for each generation.">
          <Select aria-label="AI Provider" value={provider} onChange={e => { setProvider(e.target.value as Provider); setShow(false); setMsg(null); }}>
            <option value="claude">Claude (Anthropic)</option>
            <option value="openai">ChatGPT / OpenAI</option>
          </Select>
        </Field>
        <Field label={`${provider === 'openai' ? 'OpenAI' : 'Claude'} API Key`}
          help={provider === 'openai' ? 'Create an API key at platform.openai.com/api-keys.' : 'Create an API key at console.anthropic.com.'}>
          <div className="flex gap-2">
            <Input aria-label={`${provider === 'openai' ? 'OpenAI' : 'Claude'} API Key`} type={show ? 'text' : 'password'} autoComplete="off"
              value={current.key} onChange={e => update({ key: e.target.value })}
              placeholder={current.configured ? 'Key saved — leave blank to keep it' : provider === 'openai' ? 'sk-...' : 'sk-ant-...'} />
            <Button variant="outline" aria-label={show ? 'Hide API key' : 'Show API key'} onClick={() => setShow(!show)}>{show ? '🙈' : '👁'}</Button>
          </div>
          <p className="mt-1 text-xs text-slate-500">{current.configured ? 'API key saved. Enter a new key only to replace it.' : 'No API key saved yet.'}</p>
        </Field>
        <Field label={`${names[provider]} Model`} help="Save to apply the selection. Test Connection uses the provider and model shown here.">
          <Select aria-label="AI Model" value={custom[provider] || !catalog[provider].some(m => m.id === current.model) ? '__custom' : current.model}
            onChange={e => { setCustom(prev => ({ ...prev, [provider]: e.target.value === '__custom' })); if (e.target.value !== '__custom') update({ model: e.target.value }); }}>
            {catalog[provider].map(m => <option key={m.id} value={m.id}>{m.name}{m.tier ? ` (${m.tier})` : ''}</option>)}
            <option value="__custom">Other model ID…</option>
          </Select>
          {(custom[provider] || !catalog[provider].some(m => m.id === current.model)) && <Input className="mt-2" aria-label="Custom model ID" value={current.model} onChange={e => update({ model: e.target.value })} />}
        </Field>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={save}>{busy === 'save' ? 'Saving…' : '✓ Save Settings'}</Button>
          <Button className="bg-sky-500 hover:bg-sky-600" onClick={test}>{busy === 'test' ? 'Testing…' : '⚡ Test Connection'}</Button>
        </div>
      </fieldset>
    </>}
  </Card>;
}
